import {useState, useLayoutEffect, useEffect, useCallback} from 'react';

import type {PreviewStreamController, MediaTrack} from '@pexip/media';
import {UserMediaStatus} from '@pexip/media';
import {assert} from '@pexip/utils';

import {logger} from '../logger';

const usePreviewMedia = (controller: PreviewStreamController) => {
    const [previewMedia, setPreviewMedia] = useState(controller.media);

    useLayoutEffect(
        () =>
            controller.onMediaChanged(media => {
                setPreviewMedia(media);
            }),
        [controller],
    );

    return previewMedia;
};

const areTracksMuted = (tracks: MediaTrack[] | undefined) => {
    if (!tracks || tracks.length === 0) {
        return true;
    }
    return tracks.some(track => track.stopped || track.muted) ?? true;
};
const areTracksSuspended = (tracks: MediaTrack[] | undefined) => {
    if (!tracks || tracks.length === 0) {
        return true;
    }
    return tracks.some(track => track.suspended) ?? false;
};

const usePreviewState = (controller: PreviewStreamController) => {
    const [status, setStatus] = useState(
        controller.media?.status ?? UserMediaStatus.Initial,
    );
    const [videoMuted, setVideoMuted] = useState(() =>
        areTracksMuted(controller.media?.getVideoTracks()),
    );
    const [audioMuted, setAudioMuted] = useState(() =>
        areTracksMuted(controller.media?.getAudioTracks()),
    );
    const [audioSuspended, setAudioSuspended] = useState(() =>
        areTracksSuspended(controller.media?.getAudioTracks()),
    );
    const [videoSuspended, setVideoSuspended] = useState(() =>
        areTracksSuspended(controller.media?.getVideoTracks()),
    );
    const [updatingPreviewAudio, setUpdatingPreviewAudio] = useState(false);
    const [updatingPreviewVideo, setUpdatingPreviewVideo] = useState(false);
    const [videoInputChanged, setVideoInputChanged] = useState(
        controller.videoInputChanged,
    );

    useEffect(() => {
        const subscriptions = [
            controller.onUpdatingPreviewVideo(updating => {
                if (!updating) {
                    assert(controller.media);
                    setStatus(controller.media.status);
                    setVideoInputChanged(controller.videoInputChanged);
                }
                setUpdatingPreviewVideo(updating);
            }),
            controller.onUpdatingPreviewAudio(updating => {
                if (!updating) {
                    assert(controller.media);
                    setStatus(controller.media.status);
                }
                setUpdatingPreviewAudio(updating);
            }),
            controller.onVideoMuted(muted => {
                setVideoMuted(muted ?? true);
            }),
            controller.onAudioMuted(muted => {
                setAudioMuted(muted ?? true);
            }),
            controller.onAudioSuspended(suspended => {
                setAudioSuspended(suspended ?? false);
            }),
            controller.onVideoSuspended(suspended => {
                setVideoSuspended(suspended ?? false);
            }),
            controller.onUpdatingMain(() => {
                setVideoInputChanged(controller.videoInputChanged);
            }),
        ];
        return () => {
            for (const unsub of subscriptions) {
                unsub();
            }
            subscriptions.length = 0;
        };
    }, [controller]);

    return {
        audioMuted,
        audioSuspended,
        status,
        updatingPreviewAudio,
        updatingPreviewVideo,
        videoInputChanged,
        videoMuted,
        videoSuspended,
    };
};

export const usePreviewControllerHandler = ({
    applyChanges,
    close,
    controller,
    onHandleSave,
}: {
    applyChanges: () => boolean;
    close?: (e: React.SyntheticEvent<HTMLElement>) => void;
    controller: PreviewStreamController;
    onHandleSave?: () => void;
}) => {
    const [isSaving, setSaving] = useState(false);

    const previewMedia = usePreviewMedia(controller);
    const {
        audioMuted,
        audioSuspended,
        status,
        updatingPreviewAudio,
        updatingPreviewVideo,
        videoInputChanged,
        videoMuted,
        videoSuspended,
    } = usePreviewState(controller);

    const handleSave = useCallback(
        async (e: React.SyntheticEvent<HTMLElement>) => {
            logger.debug('Handle saving settings modal');
            if (onHandleSave) {
                onHandleSave();
            }
            setSaving(true);
            const force = applyChanges();

            await controller?.applyChanges(force);
            setSaving(false);

            close?.(e);
        },
        [applyChanges, close, controller, onHandleSave],
    );

    return {
        audioMuted,
        audioSuspended,
        handleSave,
        isSaving,
        previewMedia,
        status,
        updatingPreviewAudio,
        updatingPreviewVideo,
        videoInputChanged,
        videoMuted,
        videoSuspended,
    };
};

export type PreviewControllerHandler = ReturnType<
    typeof usePreviewControllerHandler
>;
